import { useState, useEffect, useCallback } from "react";
import { TourTooltip } from "./TourTooltip";
import type { TourStep } from "../data/tourSteps";

interface TourOverlayProps {
  steps: TourStep[];
  currentStep: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onComplete: () => void;
}

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function isElementLargerThanViewport(rect: HighlightRect): boolean {
  return (
    rect.height > window.innerHeight * 0.8 ||
    rect.width > window.innerWidth * 0.8
  );
}

function computeTooltipPosition(
  rect: HighlightRect,
  placement: TourStep["placement"]
): { top: number; left: number; centered: boolean } {
  const OFFSET = 12;
  const TOOLTIP_WIDTH = Math.min(320, window.innerWidth - 32);
  const TOOLTIP_HEIGHT_ESTIMATE = 160;

  // Gdy element jest większy niż viewport, centruj tooltip na ekranie
  if (isElementLargerThanViewport(rect)) {
    return {
      top: window.innerHeight / 2 - TOOLTIP_HEIGHT_ESTIMATE / 2,
      left: window.innerWidth / 2 - TOOLTIP_WIDTH / 2,
      centered: true,
    };
  }

  switch (placement) {
    case "bottom":
      return {
        top: rect.top + rect.height + OFFSET,
        left: rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
        centered: false,
      };
    case "top":
      return {
        top: rect.top - OFFSET - TOOLTIP_HEIGHT_ESTIMATE,
        left: rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
        centered: false,
      };
    case "left":
      return {
        top: rect.top + rect.height / 2 - 80,
        left: rect.left - TOOLTIP_WIDTH - OFFSET,
        centered: false,
      };
    case "right":
      return {
        top: rect.top + rect.height / 2 - 80,
        left: rect.left + rect.width + OFFSET,
        centered: false,
      };
  }
}

export function TourOverlay({
  steps,
  currentStep,
  onNext,
  onPrev,
  onSkip,
  onComplete,
}: TourOverlayProps) {
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(
    null
  );
  const step = steps[currentStep];

  // Update highlight position (bez scrollowania)
  const updateHighlight = useCallback(() => {
    if (!step) return;
    // Przy kotwicach renderowanych równolegle w wariantach mobile/desktop (np. tabela
    // "hidden md:block" + lista "md:hidden") querySelector zwraca pierwszy w DOM -
    // jeśli on jest ukryty, rect ma zerowe wymiary i tooltip ląduje w (0,0).
    // Wybieramy więc pierwszy element z niezerowym rozmiarem.
    const elements = document.querySelectorAll(step.target);
    let visibleRect: DOMRect | null = null;
    for (const element of elements) {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        visibleRect = rect;
        break;
      }
    }
    if (visibleRect) {
      setHighlightRect({
        top: visibleRect.top - 4,
        left: visibleRect.left - 4,
        width: visibleRect.width + 8,
        height: visibleRect.height + 8,
      });
    } else {
      setHighlightRect(null);
    }
  }, [step]);

  // Scroll do elementu tylko przy zmianie kroku
  useEffect(() => {
    if (!step) return;

    // Małe opóźnienie żeby DOM się zaktualizował
    const scrollTimeout = setTimeout(() => {
      const elements = document.querySelectorAll(step.target);
      let target: Element | null = null;
      let rect: DOMRect | null = null;
      for (const el of elements) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          target = el;
          rect = r;
          break;
        }
      }
      if (target && rect) {
        const highlightRect = {
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
        };

        // Sprawdź czy element jest wystarczająco widoczny (przynajmniej 50% w viewport)
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(window.innerHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const visibilityRatio = rect.height > 0 ? visibleHeight / rect.height : 0;
        const isSufficientlyVisible = visibilityRatio > 0.5 && rect.top >= -50 && rect.top < window.innerHeight - 100;

        if (!isSufficientlyVisible) {
          // Dla dużych elementów scrolluj do góry, dla małych - centruj
          const isLargeElement = isElementLargerThanViewport(highlightRect);
          target.scrollIntoView({
            behavior: "smooth",
            block: isLargeElement ? "start" : "center",
            inline: "center",
          });
        }
      }
    }, 150);

    return () => clearTimeout(scrollTimeout);
  }, [currentStep, step]);

  // Update pozycji przy resize/scroll (bez scrollowania)
  useEffect(() => {
    updateHighlight();
    window.addEventListener("resize", updateHighlight);
    window.addEventListener("scroll", updateHighlight);
    return () => {
      window.removeEventListener("resize", updateHighlight);
      window.removeEventListener("scroll", updateHighlight);
    };
  }, [updateHighlight]);

  const handleNext = () => {
    if (currentStep >= steps.length - 1) {
      onComplete();
    } else {
      onNext();
    }
  };

  const computedPosition = highlightRect
    ? computeTooltipPosition(highlightRect, step.placement)
    : { top: window.innerHeight / 2 - 80, left: window.innerWidth / 2 - 160, centered: true };
  const tooltipWidth = Math.min(320, window.innerWidth - 32);
  const tooltipHeightEstimate = 180; // Przybliżona wysokość tooltipa
  const clampedLeft = Math.max(16, Math.min(computedPosition.left, window.innerWidth - tooltipWidth - 16));
  // Clamp top: minimum 16px od góry, maksimum tak żeby tooltip nie wychodził poza dół ekranu
  const clampedTop = Math.max(16, Math.min(computedPosition.top, window.innerHeight - tooltipHeightEstimate - 16));
  const tooltipPosition = { top: clampedTop, left: clampedLeft };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[10000] bg-black/50 transition-opacity"
        onClick={onSkip}
      />

      {/* Highlight cutout */}
      {highlightRect && (
        <div
          className="fixed z-[10000] border-2 border-primary rounded-lg pointer-events-none"
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
            transition: "top 0.2s ease-out, left 0.2s ease-out, width 0.2s ease-out, height 0.2s ease-out",
          }}
        />
      )}

      {/* Tooltip */}
      <TourTooltip
        step={step}
        currentStep={currentStep}
        totalSteps={steps.length}
        position={tooltipPosition}
        onNext={handleNext}
        onPrev={onPrev}
        onSkip={onSkip}
      />
    </>
  );
}
